import { useState } from "react";
import { PLAN_FEATURES, LOCKED_FEATURE_INFO } from "./usePlan";

const T = {
  bg: "#06090f", panel: "#0b0f1a", card: "#0f1623", border: "#1a2540",
  gold: "#c9a96e", goldLight: "#e8c98a", goldDim: "#7a6640",
  text: "#e8eaf0", textMid: "#7a8599", textDim: "#3d4a5c",
  active: "#10b981", activeBg: "#071f14", activeText: "#34d399",
  completedBg: "#13132e", completedText: "#a5b4fc", moderate: "#fbbf24",
};

const PLANS = [
  {
    key: "basic",
    label: "Basic",
    price: "₹299",
    period: "/month",
    color: "#22d3ee",
    tagline: "Perfect for solo physiotherapists",
    highlight: false,
  },
  {
    key: "pro",
    label: "Pro",
    price: "₹599",
    period: "/month",
    color: T.gold,
    tagline: "For growing clinics",
    highlight: true,
    badge: "Most Popular",
  },
  {
    key: "plus",
    label: "Plus",
    price: "₹899",
    period: "/month",
    color: "#a78bfa",
    tagline: "For established clinics",
    highlight: false,
  },
];

const ALL_FEATURES_DISPLAY = [
  { key: "patients",         label: "Patients",            },
  { key: "therapists",       label: "Therapists",          },
  { key: "dashboard",        label: "Dashboard",           },
  { key: "schedule",         label: "Schedule",            },
  { key: "knowledge",        label: "Knowledge Hub",       },
  { key: "outcomes",         label: "Outcome Measures",    },
  { key: "analytics",        label: "Analytics",           },
  { key: "progress",         label: "Progress Analytics",  },
  { key: "soap",             label: "SOAP Note Generator", },
  { key: "billing",          label: "Billing & Payments",  },
  { key: "research",         label: "Research Module",     },
  { key: "export",           label: "CSV Data Export",     },
  { key: "priority_support", label: "Priority Support",    },
  { key: "custom_branding",  label: "Custom Branding",     },
];

function getTherapistLabel(key) {
  const limits = { basic: "4", pro: "8", plus: "Unlimited" };
  return limits[key] || "Unlimited";
}

function hasFeatureInPlan(planKey, featureKey) {
  return (PLAN_FEATURES[planKey]?.features || []).includes(featureKey);
}

function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) { resolve(); return; }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = resolve;
    script.onerror = () => reject(
      new Error("Failed to load Razorpay SDK")
    );
    document.body.appendChild(script);
  });
}

const API_BASE = "";

// ── Responsive styles ────────────────────────────────
const RESPONSIVE_CSS = `
  .upgrade-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.92);
    backdrop-filter: blur(8px);
    z-index: 500;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 20px 16px 40px;
  }

  .upgrade-container {
    width: 100%;
    max-width: 960px;
    margin: 0 auto;
    animation: fadeUp 0.3s ease;
  }

  .upgrade-header {
    text-align: center;
    margin-bottom: 28px;
    padding: 0 8px;
  }

  .upgrade-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(24px, 5vw, 38px);
    font-weight: 700;
    color: #e8c98a;
    margin-bottom: 10px;
    line-height: 1.2;
  }

  .upgrade-subtitle {
    font-size: clamp(13px, 3vw, 15px);
    color: #7a8599;
    max-width: 520px;
    margin: 0 auto;
    line-height: 1.7;
  }

  .plans-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
    margin-bottom: 28px;
  }

  .plan-card {
    background: #0f1623;
    border: 1px solid #1a2540;
    border-radius: 16px;
    padding: 20px 16px;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .plan-card.highlight {
    background: linear-gradient(
      135deg,
      rgba(201,169,110,0.08),
      rgba(201,169,110,0.03)
    );
    border-color: #c9a96e;
  }

  .plan-price {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(28px, 4vw, 40px);
    font-weight: 700;
    color: #e8eaf0;
  }

  .plan-features {
    display: grid;
    gap: 7px;
    margin-bottom: 18px;
    flex: 1;
  }

  .plan-cta {
    width: 100%;
    padding: 12px;
    font-size: 13px;
    font-weight: 700;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    font-family: 'DM Sans', sans-serif;
    border: none;
    margin-top: auto;
  }

  .upgrade-footer {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 0 16px;
  }

  /* ── TABLET ── */
  @media (max-width: 768px) {
    .upgrade-overlay {
      padding: 16px 12px 40px;
    }

    .plans-grid {
      grid-template-columns: 1fr;
      gap: 12px;
      max-width: 420px;
      margin-left: auto;
      margin-right: auto;
    }

    .plan-card {
      padding: 20px;
    }

    .upgrade-title {
      font-size: 26px;
    }
  }

  /* ── MOBILE ── */
  @media (max-width: 480px) {
    .upgrade-overlay {
      padding: 12px 10px 60px;
    }

    .upgrade-header {
      margin-bottom: 20px;
    }

    .upgrade-title {
      font-size: 22px;
    }

    .upgrade-subtitle {
      font-size: 13px;
    }

    .plans-grid {
      grid-template-columns: 1fr;
      max-width: 100%;
    }

    .plan-card {
      padding: 18px 16px;
    }

    .plan-cta {
      padding: 14px;
      font-size: 14px;
    }
  }
`;

export default function UpgradeWall({
  featureKey,
  trialDaysLeft,
  isTrialExpired,
  session,
  onClose,
  onUpgradeSuccess,
}) {
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [error, setError]             = useState("");

  const featureInfo = featureKey 
    ? LOCKED_FEATURE_INFO[featureKey] 
    : null;

  async function handleUpgrade(planKey) {
    setLoadingPlan(planKey);
    setError("");

    try {
      const res = await fetch(
        `${API_BASE}/api/create-subscription`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            planKey,
            userId: session?.user?.id,
            email:  session?.user?.email,
          }),
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(
        data.error || "Failed to create subscription"
      );

      await loadRazorpayScript();

      const plan = PLANS.find((p) => p.key === planKey);

      const options = {
        key:             data.keyId,
        subscription_id: data.subscriptionId,
        name:            "PhysioCens",
        description:     `${plan?.label} Plan - Monthly`,
        image:           "/logo.png",
        prefill: { email: session?.user?.email || "" },
        theme: { color: "#c9a96e" },

        handler: async function (response) {
          console.log("✅ Payment handler fired!");
          try {
            const verifyRes = await fetch(
              `${API_BASE}/api/verify-payment`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  razorpay_payment_id:
                    response.razorpay_payment_id,
                  razorpay_subscription_id:
                    response.razorpay_subscription_id,
                  razorpay_signature:
                    response.razorpay_signature,
                  planKey,
                  userId: session?.user?.id,
                }),
              }
            );

            const verifyData = await verifyRes.json();

            if (verifyRes.ok && verifyData.success) {
              onUpgradeSuccess?.(planKey);
            } else {
              setError(
                verifyData.error || "Verification failed"
              );
            }
          } catch (err) {
            setError("Verification error: " + err.message);
          } finally {
            setLoadingPlan(null);
          }
        },

        modal: {
          ondismiss: () => {
            setLoadingPlan(null);
            setError("Payment cancelled. Try again.");
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (r) => {
        setError(`Payment failed: ${r.error.description}`);
        setLoadingPlan(null);
      });
      rzp.open();

    } catch (err) {
      setError(err.message || "Something went wrong.");
      setLoadingPlan(null);
    }
  }

  return (
    <>
      <style>{RESPONSIVE_CSS}</style>

      <div className="upgrade-overlay">
        <div className="upgrade-container">

          {/* ── HEADER ── */}
          <div className="upgrade-header">
            {isTrialExpired ? (
              <>
                <div style={{ fontSize: 44, marginBottom: 10 }}>
                  ⏰
                </div>
                <div className="upgrade-title">
                  Your Free Trial Has Ended
                </div>
                <div className="upgrade-subtitle">
                  You had full access to all features for 14 days.
                  Choose a plan to continue managing your clinic
                  with PhysioCens.
                </div>
              </>
            ) : featureInfo ? (
              <>
                <div style={{ fontSize: 44, marginBottom: 10 }}>
                  {featureInfo.icon}
                </div>
                <div className="upgrade-title">
                  {featureInfo.title}
                </div>
                <div className="upgrade-subtitle">
                  {featureInfo.description}
                </div>
                <div style={{
                  marginTop: 12,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 16px",
                  borderRadius: 20,
                  background: "rgba(201,169,110,0.1)",
                  border: `1px solid ${T.gold}`,
                }}>
                  <span style={{
                    fontSize: 12,
                    color: T.gold,
                    fontWeight: 600,
                  }}>
                    Available from{" "}
                    {featureInfo.minPlan === "pro"
                      ? "Pro" : "Plus"} plan onwards
                  </span>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 44, marginBottom: 10 }}>
                  ⚡
                </div>
                <div className="upgrade-title">
                  Upgrade PhysioCens
                </div>
                <div className="upgrade-subtitle">
                  Instant activation. Cancel anytime.
                </div>
              </>
            )}

            {/* Trial warning */}
            {!isTrialExpired &&
              trialDaysLeft > 0 &&
              trialDaysLeft <= 7 && (
              <div style={{
                marginTop: 14,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 18px",
                borderRadius: 20,
                background: "rgba(251,191,36,0.1)",
                border: `1px solid ${T.moderate}`,
              }}>
                <span style={{
                  fontSize: 13,
                  color: T.moderate,
                  fontWeight: 700,
                }}>
                  ⚠️ {trialDaysLeft} day
                  {trialDaysLeft !== 1 ? "s" : ""} left
                  in your free trial
                </span>
              </div>
            )}
          </div>

          {/* ── ERROR ── */}
          {error && (
            <div style={{
              marginBottom: 16,
              padding: "12px 16px",
              background: "rgba(248,113,113,0.08)",
              border: "1px solid rgba(248,113,113,0.3)",
              borderRadius: 10,
              color: "#f87171",
              fontSize: 13,
              textAlign: "center",
            }}>
              ⚠️ {error}
            </div>
          )}

          {/* ── PLANS GRID ── */}
          <div className="plans-grid">
            {PLANS.map((plan) => {
              const isLoading  = loadingPlan === plan.key;
              const isDisabled = loadingPlan !== null;

              return (
                <div
                  key={plan.key}
                  className={`plan-card ${
                    plan.highlight ? "highlight" : ""
                  }`}
                >
                  {/* Top accent */}
                  <div style={{
                    position: "absolute",
                    top: 0, left: 0, right: 0,
                    height: 3,
                    background: plan.color,
                  }} />

                  {/* Badge */}
                  {plan.badge && (
                    <div style={{
                      position: "absolute",
                      top: 14, right: 14,
                      fontSize: 10,
                      fontWeight: 800,
                      padding: "3px 10px",
                      borderRadius: 10,
                      background: T.gold,
                      color: "#07090e",
                    }}>
                      {plan.badge}
                    </div>
                  )}

                  {/* Plan name */}
                  <div style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: plan.color,
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    marginBottom: 8,
                    marginTop: 8,
                  }}>
                    {plan.label}
                  </div>

                  {/* Price */}
                  <div style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 4,
                    marginBottom: 4,
                  }}>
                    <span className="plan-price">
                      {plan.price}
                    </span>
                    <span style={{
                      fontSize: 12,
                      color: T.textDim,
                    }}>
                      {plan.period}
                    </span>
                  </div>

                  {/* Tagline */}
                  <div style={{
                    fontSize: 11,
                    color: T.textDim,
                    marginBottom: 14,
                  }}>
                    {plan.tagline}
                  </div>

                  {/* Therapist count */}
                  <div style={{
                    background: T.panel,
                    borderRadius: 8,
                    padding: "9px 12px",
                    marginBottom: 14,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}>
                    <span style={{
                      fontSize: 12,
                      color: T.textMid,
                    }}>
                      Therapists
                    </span>
                    <span style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: plan.color,
                    }}>
                      {getTherapistLabel(plan.key)}
                    </span>
                  </div>

                  {/* Features */}
                  <div className="plan-features">
                    {ALL_FEATURES_DISPLAY.map((f) => {
                      const included = hasFeatureInPlan(
                        plan.key, f.key
                      );
                      return (
                        <div
                          key={f.key}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <span style={{
                            fontSize: 13,
                            flexShrink: 0,
                            color: included
                              ? T.activeText
                              : T.textDim,
                          }}>
                            {included ? "✓" : "✕"}
                          </span>
                          <span style={{
                            fontSize: 12,
                            color: included
                              ? T.textMid
                              : T.textDim,
                            textDecoration: included
                              ? "none"
                              : "line-through",
                          }}>
                            {f.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {/* CTA Button */}
                  <button
                    className="plan-cta"
                    onClick={() => handleUpgrade(plan.key)}
                    disabled={isDisabled}
                    style={{
                      background: isLoading
                        ? T.panel
                        : plan.highlight
                          ? `linear-gradient(
                              135deg,
                              ${T.gold},
                              ${T.goldLight}
                            )`
                          : "transparent",
                      border: `1px solid ${
                        isLoading
                          ? T.border
                          : plan.highlight
                            ? T.gold
                            : plan.color
                      }`,
                      color: isLoading
                        ? T.textDim
                        : plan.highlight
                          ? "#07090e"
                          : plan.color,
                      opacity: isDisabled && !isLoading
                        ? 0.6 : 1,
                      cursor: isDisabled
                        ? "not-allowed"
                        : "pointer",
                    }}
                  >
                    {isLoading
                      ? "Opening Checkout..."
                      : `Upgrade to ${plan.label}`}
                  </button>
                </div>
              );
            })}
          </div>

          {/* ── FOOTER ── */}
          <div className="upgrade-footer">
            <div style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              alignItems: "center",
              gap: 8,
              padding: "10px 16px",
              borderRadius: 20,
              background: "rgba(255,255,255,0.02)",
              border: `1px solid ${T.border}`,
            }}>
              <span style={{
                fontSize: 12,
                color: T.textMid,
              }}>
                🔒 Secured by Razorpay
              </span>
              <span style={{ color: T.textDim }}>·</span>
              <span style={{
                fontSize: 12,
                color: T.textMid,
              }}>
                ⚡ Instant activation
              </span>
              <span style={{ color: T.textDim }}>·</span>
              <span style={{
                fontSize: 12,
                color: T.textMid,
              }}>
                ↩ Cancel anytime
              </span>
            </div>

            <div style={{
              fontSize: 11,
              color: T.textDim,
              textAlign: "center",
            }}>
              GST Invoice provided · Tamil Nadu compliant
              · Data always yours
            </div>

            {!isTrialExpired && onClose && (
              <button
                onClick={onClose}
                style={{
                  marginTop: 8,
                  background: "transparent",
                  border: `1px solid ${T.border}`,
                  color: T.textMid,
                  padding: "8px 20px",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontSize: 13,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Maybe Later — Continue Trial
                ({trialDaysLeft} days left)
              </button>
            )}
          </div>

        </div>
      </div>
    </>
  );
}